import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { X, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal showing email send history for a specific document (quote or invoice).
 *
 * Props:
 *   open         : boolean
 *   onClose      : () => void
 *   documentType : 'quote' | 'invoice'
 *   documentId   : id of the doc
 *   documentNumber : (optional) doc number for the header
 */
const EmailHistoryModal = ({ open, onClose, documentType, documentId, documentNumber }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/email/logs?document_id=${documentId}&document_type=${documentType}`);
        if (!cancelled) setLogs(res.data || []);
      } catch (err) {
        toast.error('Fout bij laden e-mailgeschiedenis');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, documentId, documentType]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
      data-testid="email-history-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#3da5a5]" />
            <h2 className="text-lg font-semibold text-gray-800">
              E-mailgeschiedenis — {documentNumber || ''}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <p className="text-center text-gray-500 py-6">Laden...</p>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nog geen e-mails verstuurd voor dit document</p>
            </div>
          ) : (
            logs.map(log => (
              <div
                key={log.id}
                className={`border rounded-lg p-3 text-sm ${
                  log.status === 'sent' ? 'border-green-200 bg-green-50' :
                  log.status === 'failed' ? 'border-red-200 bg-red-50' :
                  'border-gray-200 bg-gray-50'
                }`}
                data-testid={`email-log-${log.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {log.status === 'sent' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {log.status === 'failed' && <XCircle className="w-4 h-4 text-red-600" />}
                    {log.status === 'pending' && <Clock className="w-4 h-4 text-gray-400" />}
                    <span className="font-medium text-gray-800">{log.recipient}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {log.sent_at ? new Date(log.sent_at).toLocaleString('nl-NL') : ''}
                  </span>
                </div>
                <p className="text-xs text-gray-600"><strong>Onderwerp:</strong> {log.subject}</p>
                {log.cc?.length > 0 && <p className="text-xs text-gray-500"><strong>CC:</strong> {log.cc.join(', ')}</p>}
                {log.bcc?.length > 0 && <p className="text-xs text-gray-500"><strong>BCC:</strong> {log.bcc.join(', ')}</p>}
                {log.sent_by_name && <p className="text-xs text-gray-400">Verzonden door: {log.sent_by_name}</p>}
                {log.status === 'failed' && log.error && (
                  <p className="text-xs text-red-700 mt-1"><strong>Fout:</strong> {log.error}</p>
                )}
                {log.has_attachment && <p className="text-xs text-blue-600 mt-1">📎 Met PDF-bijlage</p>}
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t bg-gray-50 rounded-b-2xl">
          <Button variant="outline" onClick={onClose}>Sluiten</Button>
        </div>
      </div>
    </div>,
    window.document.body
  );
};

export default EmailHistoryModal;
